import React from "react";
import { Card, CardContent, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import ComponentHeaderTitles from "../ComponentHeaderTitles";
import ComponentHeaderDescriptions from "../ComponentHeaderDescriptions";
import { useEffect } from "react";
import { head } from "lodash";

import Button from "@mui/material/Button";
/**
 * Enum for the chosen product.
 * @readonly
 * @enum {string}
 */
const ChosenProduct = {
    GAS: "Gas",
    ELECTRICITEIT: "Electriciteit",
    GAS_ELECTRICITEIT: "Gas & Electriciteit"
};



/**
 * A component that allows the user to select a product from a list of options.
 *
 * @param {function} setChosenProduct - A function to update the chosen product.
 * @returns {JSX.Element} The ProductKiezer component.
 */
function ProductKiezer({
    previousChosenProduct,
    setChosenProduct,
    availableMeterTypes,
    postcode,
    housenumber,
    suffix,
    streetName,
    cityName,
    onUpdateValidationErrors,
    setMultipleEanCodesForProducts
    })
    {
    const [selectedProduct, setSelectedProduct] = React.useState(previousChosenProduct);


    const isGasAvailable = checkIfGasIsAvailable();
    const isElectriciteitAvailable = checkIfELKIsAvailable();

    /**
     * Effect to run on component mount and whenever previousChosenProduct changes.
     * Is responsible for setting the selectedProduct state.
     * TODO: Could probably be simplified.
     */
    useEffect(() => {
        if (previousChosenProduct.includes(ChosenProduct.GAS) && previousChosenProduct.includes(ChosenProduct.ELECTRICITEIT)) {
            setSelectedProduct(ChosenProduct.GAS_ELECTRICITEIT);
        } else if (previousChosenProduct.includes(ChosenProduct.GAS)) {
            setSelectedProduct(ChosenProduct.GAS);
        } else if (previousChosenProduct.includes(ChosenProduct.ELECTRICITEIT)) {
            setSelectedProduct(ChosenProduct.ELECTRICITEIT);
            // If previousChosenProduct is empty, we want to default to Gas & Electriciteit
        } else {
            setSelectedProduct(ChosenProduct.GAS_ELECTRICITEIT)
            setChosenProduct([ChosenProduct.GAS, ChosenProduct.ELECTRICITEIT]);
        }

        if (!isGasAvailable && isElectriciteitAvailable) {
            setSelectedProduct(ChosenProduct.ELECTRICITEIT);
            setChosenProduct([ChosenProduct.ELECTRICITEIT]);
        } else if (isGasAvailable && !isElectriciteitAvailable) {
            setSelectedProduct(ChosenProduct.GAS);
            setChosenProduct([ChosenProduct.GAS]);
        }

    }, [previousChosenProduct]);

    /**
     * Checks if the address has a GAS product available.
     * The check is done by iterating through each meter type in availableMeterTypes.
     * If the address matches, the function checks if the product is GAS.
     *
     * @returns True if the address has a GAS product available, false otherwise.
     */
    function checkIfGasIsAvailable() {
        // Iterate through each meter type in availableMeterTypes
        for (let meter of availableMeterTypes) {
            // Check if the address matches
            if (meter.address.postalCode == postcode &&
                meter.address.streetNumber == housenumber &&
                (suffix === undefined || meter.address.streetNumberAddition == suffix)) {
                // Check if the product is GAS
                if (meter.product === "GAS") {
                    return true;
                }
            }
        }
        // If no match is found, return false
        return false;
    }

    /**
     * Checks if the address has an ELK product available.
     * The check is done by filtering out the meter types that do not match the address.
     * Then, the function checks if there's an ELK product among the filtered meter types.
     *
     * @returns True if the address has an ELK product available, false otherwise.
     */
    function checkIfELKIsAvailable() {
        // Filter out the meter types that do not match the address
        availableMeterTypes = availableMeterTypes.filter(meter => {
            return meter.address.postalCode == postcode &&
                   meter.address.streetNumber == housenumber &&
                   (meter.address.streetNumberAddition == suffix);
        });

        // Check if there's an ELK product among the filtered meter types
        for (let meter of availableMeterTypes) {
            if (meter.product === "ELK") {
                return true;
            }
        }
        return false;
    }


    /**
     * This is used to detect if the availableMeterTypes object has multiple EAN codes for a given product.
     * If this is the case, the user has to select the correct EAN code for the product (happens in the MultipleEanSelection component)
     * In that case, this sets the setMultipleEanCodesForProducts state to an object containing the product type as key
     * and an array of EAN codes as value.
     * If there is only one EAN code for both products, this sets the setMultipleEanCodesForProducts state to an empty object.
     *
     */
    useEffect(() => {
        const eanByProduct = {};

        availableMeterTypes.forEach(meter => {
            if (meter.address.postalCode == postcode &&
                meter.address.streetNumber == housenumber &&
                (meter.address.streetNumberAddition == suffix)) {

                // Check if the product type already exists in the eanByProduct object
                if (!eanByProduct[meter.product]) {
                    eanByProduct[meter.product] = [];
                }
                // Push the EAN code to the corresponding product type array
                eanByProduct[meter.product].push(meter.ean);
            }
        });

        // Create a new object for products with multiple EAN codes
        const multipleEanCodesForProduct = {};
        Object.keys(eanByProduct).forEach(product => {
            if (eanByProduct[product].length > 1) {
                multipleEanCodesForProduct[product] = eanByProduct[product];
            }
        });
        setMultipleEanCodesForProducts(multipleEanCodesForProduct);
    }, [postcode, housenumber, suffix]);







    /**
     * Handles the click event when a product card is clicked.
     *
     * @param {string} product - The selected product.
     */
    const handleCardClick = (product) => {
        setSelectedProduct(product);
        // we want to set the chosen product in the parent component. This needs to be an array.
        // if Gas or Electriciteit is selected, we want [Gas] or [Electriciteit]
        // if Gas & Electriciteit is selected, we want [Gas, Electriciteit]
        if (product === ChosenProduct.GAS_ELECTRICITEIT) {
            setChosenProduct([ChosenProduct.GAS, ChosenProduct.ELECTRICITEIT]);
        } else {
            setChosenProduct([product]);
        }
    };

    /**
     * The API returns the city name in all caps. This function capitalizes the first letter of the city name and lowercases the rest.
     *
     * @param {*} string A string to capitalize the first letter of.
     * @returns The string with the first letter capitalized and the rest lowercased.
     */
    function capitalizeFirstLetter(string) {
        if (string === undefined || string === null) {
            return "";
        }
        return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
    }

        function formatAddress({ streetName, housenumber, suffix, postcode, cityName }) {
            const addressSuffix = suffix && suffix !== "Geen" ? suffix : "";

            // if suffix is not "" then add a space before the suffix
            const addressSuffixWithSpace = addressSuffix? ` ${addressSuffix}` : "";

            if (streetName) {
                // Streetname is beschikbaar, dus toon volledige adres
                return `${streetName} ${housenumber}${addressSuffixWithSpace}, ${postcode}, ${capitalizeFirstLetter(cityName || "")}`;
            } else {
                // Alleen postcode en huisnummer beschikbaar
                return `${postcode} ${housenumber}${addressSuffixWithSpace}`;
            }
        }
    /**
     * If neither gas nor electriciteit is available, we want to show a message to the user.
     * This is run whenever isGasAvailable or isElectriciteitAvailable changes.
     */
    useEffect(() => {
        // if neither gas nor electriciteit is available, we want to show an error
        if (!isGasAvailable && !isElectriciteitAvailable) {
            onUpdateValidationErrors(["Op dit adres kunnen wij geen gas of elektriciteit leveren. Neem contact met ons op als u nadere hulp nodig heeft.", false]);
        } else {
            onUpdateValidationErrors([]);
        }
    }, [isGasAvailable, isElectriciteitAvailable]);


        const headerDescription = (
            <div>
                Wij kunnen op het adres <span className="address">{formatAddress({ streetName, housenumber, suffix, postcode, cityName })}</span> de volgende producten leveren:
            </div>
        );
    /**
     * Returns the rendered component.
     * Only renders the available products.
     */
    return (
        <div class="ProductKiezer">
            <ComponentHeaderTitles title="Welk energieproduct wil je afnemen?"></ComponentHeaderTitles>
            {(isGasAvailable || isElectriciteitAvailable) && (
                <ComponentHeaderDescriptions description={headerDescription}>
                </ComponentHeaderDescriptions>
            )}
            <FormControl component="fieldset">
                <RadioGroup
                    name="product"
                    value={selectedProduct}
                    onChange={(e) => handleCardClick(e.target.value)}
                >
                    {isGasAvailable && isElectriciteitAvailable && (
                        <Card
                            className={selectedProduct === ChosenProduct.GAS_ELECTRICITEIT ? "selected" : ""}
                            onClick={() => handleCardClick(ChosenProduct.GAS_ELECTRICITEIT)}
                        >
                            <CardContent>
                                <FormControlLabel
                                    value={ChosenProduct.GAS_ELECTRICITEIT}
                                    control={<Radio />}
                                    label={
                                        <div className="icon-text-wrapper">
                                            <div className="icon-as-gas-stroom icon-size"></div>
                                            <Typography variant="h5" component="h2">
                                                Gas & Stroom
                                            </Typography>
                                        </div>
                                    }
                                />
                            </CardContent>
                        </Card>
                    )}
                    {isGasAvailable && (
                        <Card
                            className={selectedProduct === ChosenProduct.GAS ? "selected" : ""}
                            onClick={() => handleCardClick(ChosenProduct.GAS)}
                        >
                            <CardContent>
                                <FormControlLabel
                                    value={ChosenProduct.GAS}
                                    control={<Radio />}
                                    label={
                                        <div className="icon-text-wrapper">
                                            <div className="icon-as-gas icon-size"></div>
                                            <Typography variant="h5" component="h2">
                                                Gas
                                            </Typography>
                                        </div>
                                    }
                                />
                            </CardContent>
                        </Card>
                    )}
                    {isElectriciteitAvailable && (
                        <Card
                            className={selectedProduct === ChosenProduct.ELECTRICITEIT ? "selected" : ""}
                            onClick={() => handleCardClick(ChosenProduct.ELECTRICITEIT)}
                        >
                            <CardContent>
                                <FormControlLabel
                                    value={ChosenProduct.ELECTRICITEIT}
                                    control={<Radio />}
                                    label={
                                        <div className="icon-text-wrapper">
                                            <div className="icon-as-stroom icon-size"></div>
                                            <Typography variant="h5" component="h2">
                                                Stroom
                                            </Typography>
                                        </div>
                                    }
                                />
                            </CardContent>
                        </Card>
                    )}
                </RadioGroup>
                {(!isGasAvailable || !isElectriciteitAvailable) && (
                <div>
                    <Typography variant="body1" color="textSecondary">
                        {
                            `Helaas moeten we je mededelen dat we op het adres `
                        }
                        <span className="address">
           {formatAddress({ streetName, housenumber, suffix, postcode, cityName })}
        </span> <span className="product">
                        {!isGasAvailable && !isElectriciteitAvailable ?
                            ` geen gas en of elektriciteit ` :
                            !isGasAvailable ? ` geen gas ` :
                                ` geen elektriciteit `
                        }
                    </span>
                        kunnen leveren. Mocht je aanvullende hulp nodig hebben, aarzel dan niet om contact met ons op te nemen.
                    </Typography>
                    <a href="/klantenservice" className="btn btn-primary btn-klantenservice" role="button">
                        Neem contact op
                    </a>
                </div>)}


            </FormControl>
        </div>
    );
}
ProductKiezer.displayName = "ProductKiezer";
export default ProductKiezer;
